<template>
  <div class="form-group">
    <div class="d-flex justify-content-between">
      <label :for="inputId" v-if="label" class="input-label">
        {{ label }}
      </label>
      <v-popover
        v-if="$slots['info-popover']"
        :delay="100"
        placement="top"
        popoverClass="text-input-popover"
      >
        <font-awesome-icon :icon="['far', 'info-circle']" />
        <template slot="popover">
          <slot name="info-popover" />
        </template>
      </v-popover>
    </div>
    <ValidationProvider
      tag="div"
      :rules="rules"
      :name="inputName"
      :vid="vid"
      v-slot="{ errors }"
    >
      <div class="input-group" :class="{ 'is-invalid': errors.length }">
        <div class="input-group-prepend" v-if="$slots['prepend']">
          <span class="input-group-text">
            <slot name="prepend" />
          </span>
        </div>
        <input
          :type="type"
          :id="inputId"
          class="form-control"
          v-bind:value="value"
          v-on:input="$emit('input', $event.target.value)"
          @blur="checkChangedOnBlur"
          :class="{ 'is-invalid': errors.length }"
          :placeholder="placeholder"
          :autocomplete="autocomplete"
          :disabled="disabled"
          v-bind="$attrs"
        />
        <div class="input-group-append" v-if="$slots['append']">
          <span class="input-group-text">
            <slot name="append" />
          </span>
        </div>
      </div>
      <div class="invalid-feedback" v-show="showErrors && errors.length">
        <span v-for="error in errors" :key="error">
          {{ error }}
        </span>
      </div>
      <div class="invalid-feedback" v-show="!errors.length && infoError">
        <span>
          {{ infoError }}
        </span>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import { extend, ValidationProvider } from 'vee-validate';
import i18n from '@/web/utils/localization';
import {
  required,
  email,
  min,
  min_value,
  max,
  max_value,
  is,
  regex,
} from 'vee-validate/dist/rules';

const isValidUrl = enteredUrl => {
  if (!enteredUrl) return false;
  try {
    const url = new URL(enteredUrl);
    return (
      url.protocol === 'http:' ||
      url.protocol === 'https:' ||
      url.protocol === 'mailto:'
    );
  } catch (_) {
    return false;
  }
};

extend('email', email);
extend('required', required);
extend('min', min);
extend('min_value', min_value);
extend('max', max);
extend('max_value', max_value);
extend('is', {
  ...is,
  message: i18n.t('GENERAL.ERROR.INPUT_IS_INCORRECT'),
});
extend('regex', regex);
extend('password', {
  params: ['target'],
  validate(value, { target }) {
    return value === target;
  },
  message: i18n.t('GENERAL.ERROR.PASSWORD_CONFIRMATION_DOES_NOT_MATCH'),
});
extend('url', {
  validate(value) {
    if (value) {
      return isValidUrl(value);
    }

    return false;
  },
  message: () => {
    return '';
  },
});
extend('min_currency', {
  params: ['min', 'amount'],
  validate(value, { min }) {
    return value >= parseFloat(min);
  },
  message: (label, { amount }) => {
    return i18n.t('GENERAL.ERROR.THE_LABEL_FIELD_MUST_BE_AMOUNT_OR_MORE', {
      label,
      amount,
    });
  },
});

export default {
  name: 'HMTextInput',
  components: {
    ValidationProvider,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    name: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    vid: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: 'text',
    },
    autocomplete: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    infoError: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    inputName() {
      return this.name || this.label;
    },
    inputId() {
      return this.inputName.toLowerCase().replaceAll(' ', '-');
    },
  },
  data() {
    return {
      initialValue: null,
    };
  },
  methods: {
    checkChangedOnBlur() {
      if (this.initialValue !== this.value) {
        this.$emit('changedOnBlur');
        this.initialValue = this.value;
      }
    },
  },
  mounted() {
    this.initialValue = this.value;
  },
};
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

.tooltip.popover.text-input-popover {
  border: none;

  .popover-inner {
    background-color: $charcoal;
    color: white;
    padding: 0.7rem;
  }

  .popover-arrow {
    border-color: $charcoal;
  }
}

.input-label {
  text-transform: capitalize;
}
</style>
